import { EventType } from '@atlas/types'
import { useEffect } from 'react'
import { logger } from '@/lib/logger'
import { RealtimeConnectionState, subscribeRealtime, useRealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { fileService } from '@/services/file.service'
import { getItems, refreshItemFromServer, subscribe, UploadItemStatus } from './upload-store'

const log = logger.child('upload-realtime')

const POLL_MS = 4000
/**
 * Give up on one file after ~2 minutes of polling. Concerns are usually seconds;
 * a file still moving after this is almost certainly one whose message is in the
 * DLQ, which no amount of polling will resolve.
 */
const MAX_POLLS_PER_FILE = 30

/** Safety-net cadence while the push connection is up — rare on purpose. */
const SAFETY_NET_POLL_MS = 30_000
const SAFETY_NET_MAX_POLLS = 10

/**
 * Keeps in-flight uploads' concern maps current, by push when the realtime
 * connection is up and by polling when it isn't.
 *
 * Both paths call the identical refetch-and-replace (`refreshItemFromServer`),
 * so a push and a poll racing on the same file converge on the same answer
 * instead of one clobbering the other with staler data. The event payload is
 * never inspected — only the server's answer to `GET /files/{id}` decides what
 * a concern's state is (ADR-0020).
 *
 * Mounted once, near the app root: the store is global, so a per-page hook
 * would stop tracking the moment the user navigated away from the page that
 * started the upload.
 */
export function useUploadRealtime(): void {
	const connectionState = useRealtimeConnectionState()
	const isPushing = connectionState === RealtimeConnectionState.CONNECTED

	// Catch up immediately on mount rather than waiting for the first push or
	// poll tick: remounting (a route change that unmounts the host) can otherwise
	// leave a tile showing state that moved on while nothing was listening.
	//
	// A full page *reload* needs nothing here — the store is in-memory, so the
	// queue is gone with it and there is no item left to reconcile.
	useEffect(() => {
		for (const fileId of inFlightFileIds()) {
			void refreshItemFromServer(fileId, fileService)
		}
	}, [])

	useEffect(() => {
		log.debug('connection state changed', { connectionState, isPushing })
	}, [connectionState, isPushing])

	useEffect(() => (isPushing ? watchByPush() : undefined), [isPushing])

	// Connected still gets a slow safety net, and the confirm catch-up does NOT
	// subsume it: that one stops after 3s, while observed SQS delivery latency is
	// around 8s, so most concerns complete well after it has given up. Between
	// then and the next push there is nothing else watching, and MQTT does not
	// replay — a single dropped message would leave the tile stuck for the rest
	// of the session. The cost is one request per in-flight file per 30s, only
	// while something is outstanding, capped at ten.
	useEffect(() => (isPushing ? watchByPoll(SAFETY_NET_POLL_MS, SAFETY_NET_MAX_POLLS) : watchByPoll()), [isPushing])
}

/**
 * Push path: subscribe to processing events for the in-flight files, and
 * re-subscribe whenever that set changes — the subscription is filtered by
 * entity id, so a file that starts uploading later would otherwise never be
 * covered. Returns its own teardown.
 */
function watchByPush(): () => void {
	let unsubscribe: (() => void) | undefined
	let subscribedKey = ''

	const resubscribe = () => {
		const ids = inFlightFileIds()
		const key = ids.join(',')
		if (key === subscribedKey) return
		subscribedKey = key
		unsubscribe?.()
		unsubscribe = undefined
		if (ids.length === 0) {
			log.debug('no files in flight — not subscribing')
			return
		}
		log.debug('subscribing to processing events', { fileIds: ids })
		unsubscribe = subscribeRealtime(
			{
				type: [EventType.FILE_PROCESSING_COMPLETED, EventType.FILE_PROCESSING_FAILED],
				entityRef: { id: ids },
			},
			(message) => {
				log.debug('push received', { type: message.type, fileId: message.entityRef.id })
				void refreshItemFromServer(message.entityRef.id, fileService)
			},
		)
	}

	resubscribe()
	const unwatch = subscribe(resubscribe)
	return () => {
		unwatch()
		unsubscribe?.()
	}
}

/** Poll path, used while the realtime connection is down. Returns its own teardown. */
function watchByPoll(intervalMs: number = POLL_MS, maxPolls: number = MAX_POLLS_PER_FILE): () => void {
	// Disconnected: poll the same endpoint the push handler refetches, at the
	// cadence the thumbnail hook already uses. Also used as a slow safety net
	// while CONNECTED (see the caller) — same code path, longer interval.
	//
	// Capped per file, matching `useThumbnails`' MAX_POLLS precedent. A concern
	// that never reaches a terminal state (its message died in the DLQ) would
	// otherwise keep an item at CONFIRMED forever, costing a request every 4s
	// for the rest of the session. Giving up leaves the tile on its last known
	// state — the same outcome, without the traffic.
	const pollCounts = new Map<string, number>()
	const timer = setInterval(() => {
		const inFlight = new Set(inFlightFileIds())
		// Forget a file the moment it stops being in flight, so a later RETRY gets
		// a fresh budget. Keeping the count meant a file that exhausted its polls,
		// settled, then was retried had zero poll coverage — permanently stuck on
		// its last state if that one push happened to be dropped.
		for (const fileId of pollCounts.keys()) {
			if (!inFlight.has(fileId)) pollCounts.delete(fileId)
		}
		for (const fileId of inFlight) {
			const seen = pollCounts.get(fileId) ?? 0
			if (seen >= maxPolls) continue
			pollCounts.set(fileId, seen + 1)
			void refreshItemFromServer(fileId, fileService)
		}
	}, intervalMs)
	return () => clearInterval(timer)
}

/**
 * File ids of items with concerns still moving.
 *
 * `CONFIRMED` is exactly that set: an item whose concerns have all finished
 * derives to `SETTLED` or `FINISHED_WITH_ERRORS`, so neither keeps being polled.
 * A retry demotes its item back to `CONFIRMED`, which re-enters it here.
 */
function inFlightFileIds(): string[] {
	return getItems()
		.filter((item) => item.status === UploadItemStatus.CONFIRMED && item.fileId !== null)
		.map((item) => item.fileId as string)
}
